import React from "react";
import { Table, Tag, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { ScheduleRow } from "./types";
import dayjs from "dayjs";

const { Text } = Typography;

type Props = {
  schedule: ScheduleRow[];
  loading?: boolean;
};

const AmortizationSchedule: React.FC<Props> = ({ schedule, loading }) => {
  const { t } = useTranslation();

  const formatCurrency = (value: number) => {
    if (value === null || value === undefined) return "—";
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const columns = [
    {
      title: t("Period"),
      dataIndex: "period",
      key: "period",
      width: 80,
      fixed: "left" as const,
    },
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
    },
    {
      title: t("Opening balance"),
      dataIndex: "openingBalance",
      key: "openingBalance",
      width: 140,
      align: "right" as const,
      render: formatCurrency,
    },
    {
      title: t("Principal"),
      dataIndex: "principal",
      key: "principal",
      width: 120,
      align: "right" as const,
      render: formatCurrency,
    },
    {
      title: t("Interest"),
      dataIndex: "interest",
      key: "interest",
      width: 120,
      align: "right" as const,
      render: formatCurrency,
    },
    {
      title: t("Installment"),
      dataIndex: "installment",
      key: "installment",
      width: 130,
      align: "right" as const,
      render: (value: number) => <Text strong>{formatCurrency(value)}</Text>,
    },
    {
      title: t("Fees"),
      dataIndex: "fees",
      key: "fees",
      width: 100,
      align: "right" as const,
      render: (value: number) =>
        value > 0 ? <Text type="warning">{formatCurrency(value)}</Text> : "—",
    },
    {
      title: t("Prepayments"),
      dataIndex: "prepayments",
      key: "prepayments",
      width: 120,
      align: "right" as const,
      render: (value: number) =>
        value > 0 ? <Text type="success">{formatCurrency(value)}</Text> : "—",
    },
    {
      title: t("Closing balance"),
      dataIndex: "closingBalance",
      key: "closingBalance",
      width: 140,
      align: "right" as const,
      render: (value: number) => <Text strong>{formatCurrency(value)}</Text>,
    },
    {
      title: t("Events"),
      dataIndex: "events",
      key: "events",
      width: 200,
      render: (events: string) => {
        if (!events) return "—";
        return <span style={{ fontSize: "12px" }}>{events}</span>;
      },
    },
  ];

  const summary = React.useMemo(() => {
    if (!schedule || schedule.length === 0) return undefined;

    const totalPrincipal = schedule.reduce(
      (sum, row) => sum + (row.principal || 0),
      0,
    );
    const totalInterest = schedule.reduce(
      (sum, row) => sum + (row.interest || 0),
      0,
    );
    const totalFees = schedule.reduce((sum, row) => sum + (row.fees || 0), 0);
    const totalPrepayments = schedule.reduce(
      (sum, row) => sum + (row.prepayments || 0),
      0,
    );
    const totalInstallment = schedule.reduce(
      (sum, row) => sum + (row.installment || 0),
      0,
    );

    return () => (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={3}>
            <Text strong>{t("Total")}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            <Text strong>{formatCurrency(totalPrincipal)}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <Text strong>{formatCurrency(totalInterest)}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <Text strong>{formatCurrency(totalInstallment)}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} align="right">
            <Text strong type="warning">
              {formatCurrency(totalFees)}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7} align="right">
            <Text strong type="success">
              {formatCurrency(totalPrepayments)}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={8} colSpan={2} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [schedule, t]);

  return (
    <div>
      <Table
        columns={columns}
        dataSource={schedule}
        rowKey="period"
        loading={loading}
        pagination={false}
        scroll={{ x: 1200 }}
        summary={summary || undefined}
        size="small"
        bordered
      />
    </div>
  );
};

export default React.memo(AmortizationSchedule);
